'use client';

import React, { Suspense, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import twMerge from 'utilities/src/twMerge';
import ga4 from 'tracking/src/ga4';
import IconComponent from 'ui/src/components/Icon';
import ButtonLink from 'ui/src/components/ButtonLink';
import Image from 'ui/src/components/Image';
import { AnimatePresence, motion } from 'motion/react';
import useCookieBarContext from '../cookieBar/hooks/useCookieBarContext';

type SidePopsProps = {
  className?: string;
  shouldBeControlledByParam?: boolean;
  client: {
    name: string;
    logo: string;
  };
  caseStudy: {
    title: string;
    description: string;
    link: string;
    slug: string;
  };
  children?: React.ReactNode;
};

function SidePopsContent({
  className = '',
  client,
  caseStudy,
  shouldBeControlledByParam = false,
  children = null,
}: SidePopsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasCrossedThreshold, setHasCrossedThreshold] = useState(false);
  const sidePopsRef = useRef<HTMLDivElement>(null);
  const { cookieBarState } = useCookieBarContext();
  const searchParams = useSearchParams();

  const gaEvent = (eventName: string, event: object) => {
    if (process.env.NEXT_PUBLIC_ANALYTICS_ENABLE === 'false') return;
    ga4.assignEvent(process.env.NEXT_PUBLIC_GA4, eventName, {
      ...event,
      cuSection: 'sidepop',
      cuItem: caseStudy.slug,
    });
  };

  const dismiss = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        dismiss();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof HTMLElement && !sidePopsRef.current?.contains(event.target)) {
        dismiss();
      }
    };

    const handleScroll = () => {
      if (hasCrossedThreshold || cookieBarState === 'Visible') return;
      const scrollRatio = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
      if (scrollRatio >= 0.13) {
        setHasCrossedThreshold(true);
        setTimeout(() => {
          setIsOpen(true);

          gaEvent('cu_interactive_state', {
            cuElementType: 'popup',
            cuState: 'inViewport',
          });
        }, 400);
      }
    };

    window.addEventListener('keydown', handleEsc);
    window.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleEsc);
      window.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasCrossedThreshold, cookieBarState, gaEvent]);

  const handleCtaClick = (e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (process.env.NEXT_PUBLIC_ANALYTICS_ENABLE === 'false' || !e) return;

    gaEvent('cu_user_action', {
      cuElementType: 'cta',
      cuAction: 'click',
      cuUrl: caseStudy.link,
    });

    if (!e.altKey && !e.metaKey && !e.shiftKey && !e.ctrlKey && e.button === 0) {
      e.preventDefault();
      window.location.href = e.currentTarget.href;
    }
  };

  const handleToggleClick = () => {
    const state = isOpen ? 'dismiss' : 'reveal';

    gaEvent('cu_interactive_state', {
      cuElementType: 'popup',
      cuState: state,
    });

    setIsOpen(!isOpen);
  };

  if (cookieBarState === 'Visible') {
    return null;
  }

  if (shouldBeControlledByParam && searchParams.get('sidepop') !== 'true') {
    return null;
  }

  return (
    <div className={twMerge('fixed bottom-4 left-4 z-50', 'lgS:bottom-8 lgS:left-8', className)}>
      <div ref={sidePopsRef} className="relative">
        <button
          type="button"
          aria-label="Open side pops"
          className={twMerge(
            'bg-site-neutrals-900 text-site-neutrals-0 flex h-10 w-10 items-center justify-center rounded-md shadow-md transition-all duration-300',
            'lgS:h-12 lgS:w-12',
            hasCrossedThreshold ? 'scale-100' : 'scale-0',
            !isOpen && 'hover:scale-105 hover:shadow-xl',
          )}
          onClick={handleToggleClick}
        >
          <IconComponent
            iconName="Close"
            className={twMerge('bg-site-neutrals-0 transition-all duration-300', isOpen ? '' : '-rotate-45')}
          />
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              layout
              key="side-pops"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={twMerge(
                'absolute bottom-[3.125rem] left-0',
                'from-site-neutrals-900 to-site-neutrals-600 w-[21.5rem] origin-bottom-left overflow-hidden rounded-lg bg-gradient-to-r p-1',
                'lgS:bottom-[3.625rem]',
              )}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.3, ease: 'easeInOut', delay: 0.2 } }}
                exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeInOut' } }}
              >
                <header className=" flex items-center justify-between pb-2 pl-4 pr-3 pt-1">
                  <div className="flex items-center gap-2.5">
                    <Image
                      src="https://assets.bairesdev.com/image/upload/www/static/white-bd_1775661160.svg"
                      alt="BairesDev logo"
                      width={24}
                      height={24}
                    />

                    <div className="bg-site-neutrals-50 h-3 w-px" />

                    <Image
                      src={client.logo}
                      width={80}
                      height={40}
                      alt={client.name}
                      className="h-[2.5rem] w-[5rem]"
                    />
                  </div>

                  <ButtonLink
                    href={caseStudy.link}
                    prefetch={false}
                    variantColor="Green"
                    size="Small"
                    iconName="ArrowForward"
                    iconSize="IconSM"
                    onClick={handleCtaClick}
                  >
                    Case Study
                  </ButtonLink>
                </header>

                <div className="bg-site-neutrals-0 flex flex-col gap-2 rounded p-4">
                  <p className="site-paragraph-l text-site-neutrals-900 font-bold">{caseStudy.title}</p>
                  <p className="site-paragraph-s text-site-neutrals-600">{caseStudy.description}</p>
                  {children}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function SidePops(props: SidePopsProps) {
  return (
    <Suspense fallback={null}>
      <SidePopsContent {...props} />
    </Suspense>
  );
}
